import React, { useEffect, useRef, useState } from "react";
import { auth, db } from "../firebase/firebaseConfig";
import { doc, getDoc, updateDoc, query, where, collection, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import ParticipationList from "../components/ParticipationList";
import AddParticipationForm from "../components/AddParticipationForm";
import EditParticipationForm from "../components/EditParticipationForm";
import ClubInfoSection from "../components/ClubInfoSection";
import FundraiserSection from "../components/FundraiserSection";
import SidebarNav from "../components/SidebarNav";
import PeopleSection from "../components/PeopleSection";
import HamburgerMenu from "../components/HamburgerMenu";
import DashboardAnalytics from "../components/DashboardAnalytics";

import "../styles/TeacherDashboard.css";

export default function TeacherDashboard() {
    const [teacher, setTeacher] = useState(null);
    const [participations, setParticipations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingIndex, setEditingIndex] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [activeTab, setActiveTab] = useState("dashboard");
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const sidebarRef = useRef(null);
    const [analytics, setAnalytics] = useState({
        totalParticipations: 0,
        totalTeachers: 0,
        totalStudents: 0,
        totalFundraisers: 0,
        totalDonations: 0
    });



    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const ref = doc(db, "teachers", user.uid);
                const snap = await getDoc(ref);
                if (snap.exists()) {
                    const teacherData = snap.data();
                    setTeacher(teacherData);
                    fetchParticipations(teacherData.schoolId, teacherData.clubId);
                    fetchAnalyticsData(teacherData.schoolId, teacherData.clubId);

                } else {
                    console.warn("Teacher data not found in Firestore.");
                }
                setLoading(false);
            } else {
                navigate("/login");
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    const fetchParticipations = async (schoolId, clubId) => {
        const clubRef = doc(db, "schools", schoolId, "clubs", clubId);
        const snap = await getDoc(clubRef);
        if (snap.exists()) {
            const data = snap.data();
            const list = data.participations || [];
            const sorted = list.sort((a, b) => {
                const dateA = a.date_start?.seconds || 0;
                const dateB = b.date_start?.seconds || 0;
                return dateB - dateA; // Descending
            });
            setParticipations(sorted.map((item, i) => ({ id: i, ...item })));
        } else {
            console.warn("Club data not found.");
            setParticipations([]);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            const sidebar = sidebarRef.current;
            const isClickInsideSidebar = sidebar && sidebar.contains(e.target);
            const isClickOnHamburger = e.target.closest(".hamburger-menu");

            if (!isClickInsideSidebar && !isClickOnHamburger) {
                setIsMenuOpen(false);
            }
        };

        if (isMenuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isMenuOpen]);



    const handleAddNew = async (newItem) => {
        const clubRef = doc(db, "schools", teacher.schoolId, "clubs", teacher.clubId);
        const snap = await getDoc(clubRef);
        if (!snap.exists()) return;

        const data = snap.data();
        const updatedArray = [...(data.participations || []), newItem];

        await updateDoc(clubRef, { participations: updatedArray });

        const sorted = updatedArray.sort((a, b) => {
            const dateA = a.date_start?.seconds || 0;
            const dateB = b.date_start?.seconds || 0;
            return dateB - dateA;
        });
        setParticipations(sorted.map((item, i) => ({ id: i, ...item })));
        setShowAddForm(false);
    };

    const handleSaveEdit = async (index, updatedItem) => {
        const clubRef = doc(db, "schools", teacher.schoolId, "clubs", teacher.clubId);
        const snap = await getDoc(clubRef);
        if (!snap.exists()) return;

        const data = snap.data();
        const updatedArray = [...(data.participations || [])];
        updatedArray[index] = updatedItem;

        await updateDoc(clubRef, { participations: updatedArray });

        const sorted = updatedArray.sort((a, b) => {
            const dateA = a.date_start?.seconds || 0;
            const dateB = b.date_start?.seconds || 0;
            return dateB - dateA;
        });
        setParticipations(sorted.map((item, i) => ({ id: i, ...item })));
        setEditingIndex(null);
    };

    const handleDelete = async (indexToDelete) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this participation?");
        if (!confirmDelete) return;

        const clubRef = doc(db, "schools", teacher.schoolId, "clubs", teacher.clubId);
        const snap = await getDoc(clubRef);
        if (!snap.exists()) return;

        const data = snap.data();
        const updated = (data.participations || []).filter((_, i) => i !== indexToDelete);

        await updateDoc(clubRef, { participations: updated });

        const sorted = updated.sort((a, b) => {
            const dateA = a.date_start?.seconds || 0;
            const dateB = b.date_start?.seconds || 0;
            return dateB - dateA;
        });
        setParticipations(sorted.map((item, i) => ({ id: i, ...item })));
    };


    const fetchAnalyticsData = async (schoolId, clubId) => {
        // Get club doc to read participations, teachers, students
        const clubRef = doc(db, "schools", schoolId, "clubs", clubId);
        const clubSnap = await getDoc(clubRef);
        if (!clubSnap.exists()) return;

        const clubData = clubSnap.data();

        const totalParticipations = (clubData.participations || []).length;
        const totalTeachers = (clubData.teachers || []).length;
        const totalStudents = (clubData.students || []).length;

        // ✅ Fetch fundraisers belonging to this club
        const fundraiserRef = collection(db, "schools", schoolId, "fundraisers");
        const q = query(fundraiserRef, where("club_id", "==", clubId));
        const snap = await getDocs(q);

        const fundraisers = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const totalFundraisers = fundraisers.length;

        // ✅ Loop fundraisers to fetch donors & calculate total donations
        let totalDonations = 0;

        for (const f of fundraisers) {
            const donorRef = collection(db, "schools", schoolId, "fundraisers", f.id, "donors");
            const donorSnap = await getDocs(donorRef);

            const sumForThisFundraiser = donorSnap.docs.reduce((sum, d) => {
                const data = d.data();
                if (data.confirmed === "confirmed") {
                    return sum + Number(data.amount || 0);
                }
                return sum;
            }, 0);


            totalDonations += sumForThisFundraiser;
        }

        setAnalytics({
            totalParticipations,
            totalTeachers,
            totalStudents,
            totalFundraisers,
            totalDonations
        });
    };




    if (loading) return <p>Loading...</p>;



    return (
        <div className="dashboard-layout">
            <HamburgerMenu
                isOpen={isMenuOpen}
                onClick={() => {
                    console.log("setIsMenuOpen triggered");
                    setIsMenuOpen(prev => !prev);
                }}
            />
            <SidebarNav
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onClose={() => setIsMenuOpen(false)}
                ref={sidebarRef}
                isMenuOpen={isMenuOpen}
            />



            <div className="admin-main-content">

                <div className="section-content ">
                    {activeTab === "dashboard" && (
                        <>
                            <h2>Welcome, {teacher.name}</h2>
                            <DashboardAnalytics
                                totalParticipations={analytics.totalParticipations}
                                totalTeachers={analytics.totalTeachers}
                                totalStudents={analytics.totalStudents}
                                totalFundraisers={analytics.totalFundraisers}
                                totalDonations={analytics.totalDonations}
                            />
                        </>
                    )}
                    {activeTab === "about" && (
                        <>
                            <h3>About This Club</h3>
                            <ClubInfoSection schoolId={teacher.schoolId} clubId={teacher.clubId} />
                        </>
                    )}

                    {activeTab === "participations" && (
                        <>
                            <h3>Club Participations</h3>
                            {!showAddForm && editingIndex === null && (
                                <button onClick={() => setShowAddForm(true)} style={{ marginBottom: 20 }}>
                                    + Add Participation
                                </button>
                            )}
                            {showAddForm && editingIndex === null && (
                                <AddParticipationForm onAdd={handleAddNew} onCancel={() => setShowAddForm(false)} />
                            )}
                            {editingIndex !== null && !showAddForm && (
                                <EditParticipationForm
                                    data={participations[editingIndex]}
                                    index={editingIndex}
                                    onSave={handleSaveEdit}
                                    onCancel={() => setEditingIndex(null)}
                                />
                            )}
                            {!showAddForm && editingIndex === null && (
                                <ParticipationList
                                    participations={participations}
                                    onEdit={(index) => setEditingIndex(index)}
                                    onDelete={handleDelete}
                                    editingIndex={editingIndex}
                                    onSave={handleSaveEdit}
                                    onCancel={() => setEditingIndex(null)}
                                />
                            )}
                        </>
                    )}
                    {activeTab === "fundraisers" && (
                        <FundraiserSection schoolId={teacher.schoolId} clubId={teacher.clubId} />
                    )}
                    {activeTab === "people" && (
                        <PeopleSection schoolId={teacher.schoolId} clubId={teacher.clubId} />
                    )}
                </div>
            </div>
        </div>
    );

} 